import type React from "react";
import { Avatar, Box, Typography } from "@mui/material";
import type { DeceasedPerson } from "../../../../../../../../bookings/types/edit-booking";
import type { IStreamDetails } from "../../../../types/memorial-types";
import sunsetbackground from "./background.jpeg";
import CountdownTimer from "../../../../shared-ui/CountdownTimer";

function formatHeroDate(dateStr?: string | null): string {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

interface HeroHeaderProps {
  stream: IStreamDetails;
  deceasedData: DeceasedPerson | null;
  companyLogoUrl?: string;
}

const HeroHeader: React.FC<HeroHeaderProps> = ({ stream, deceasedData }) => {
  const deceased = stream.booking.deceased[0];
  const fullName = [
    deceased?.first_name,
    deceased?.second_name,
    deceased?.surname,
  ]
    .filter(Boolean)
    .join(" ");

  const photoSrc =
    deceasedData?.person_image || deceased?.person_image || undefined;
  const dob = deceasedData?.date_of_birth || deceased?.date_of_birth;
  const dod = deceasedData?.date_of_death || deceased?.date_of_death;

  const eventDate =
    stream?.service?.date_of_service ||
    stream?.event_details?.start_date_of_event ||
    stream?.booking?.events?.[0]?.start_date_of_event;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: 260, md: 380 },
        backgroundImage: `url(${sunsetbackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to right, rgba(10,6,20,0.82) 0%, rgba(10,6,20,0.55) 55%, rgba(10,6,20,0.15) 100%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 3, md: 8 },
        }}
      >
        <Box sx={{ color: "white", maxWidth: { xs: "60%", md: "55%" } }}>
          <Typography
            sx={{
              fontStyle: "italic",
              fontFamily: '"Dancing Script", "Freehand", cursive',
              fontSize: { xs: "1.1rem", md: "1.5rem" },
              color: "rgba(255,255,255,0.85)",
              mb: 0.5,
            }}
          >
            In remembrance of
          </Typography>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "3rem" },
              color: "white",
              lineHeight: 1.1,
              mb: 2,
            }}
          >
            {fullName}
          </Typography>

          <Typography>
            {formatHeroDate(dod)} - {formatHeroDate(dob)}{" "}
          </Typography>

          {eventDate && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: { xs: "center", md: "flex-start" } }}>
              <CountdownTimer eventDate={eventDate} />
            </Box>
          )}
        </Box>

        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            src={photoSrc}
            variant="rounded"
            sx={{
              width: { xs: 110, md: 250 },
              height: { xs: 110, md: 250 },
              borderRadius: "50px",
              border: "6px solid white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HeroHeader;
